import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { CLASSES, credsFormulaFor, debtFormulaFor, formulaLabel, CLASS_STARTERS } from "@/lib/character";

function statLine(cls) {
  const parts = [];
  const map = { presence: "PRE", strength: "STR", agility: "AGI", toughness: "TOU", knowledge: "KNO" };
  for (const [k, v] of Object.entries(cls.stat_rolls || {})) {
    if (typeof v.mod === "number" && v.mod !== 0) {
      parts.push(`${map[k]} ${v.mod > 0 ? `+${v.mod}` : v.mod}`);
    }
  }
  return parts.join(" · ");
}

export default function ClassPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="char-class-select"
        onClick={() => setOpen(true)}
        className="cy-input mt-1 flex items-center justify-between text-left w-full"
      >
        <span className={value ? "text-[color:var(--text-primary)]" : "text-[color:var(--text-muted)]"}>
          {value || "// Choose your doom"}
        </span>
        <ChevronDown size={16} className="text-[color:var(--accent-yellow)] ml-2 shrink-0" />
      </button>
      {value && (
        <div className="mt-1 text-[10px] font-mono text-[color:var(--text-muted)]">
          {CLASSES.find((c) => c.name === value)?.desc}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/85 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              transition={{ type: "spring", damping: 22 }}
              className="relative panel panel-yellow w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
              data-testid="class-picker-modal"
            >
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
                <div className="font-display font-black uppercase tracking-tighter text-xl sm:text-2xl">
                  CLASS // DOSSIER
                </div>
                <button data-testid="class-picker-close" onClick={() => setOpen(false)} aria-label="Close" className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {CLASSES.map((c) => {
                  const accentBorder =
                    c.accent === "magenta" ? "border-[color:var(--accent-magenta)]" :
                    c.accent === "cyan"    ? "border-[color:var(--accent-cyan)]" :
                                             "border-[color:var(--accent-yellow)]";
                  const accentText =
                    c.accent === "magenta" ? "text-[color:var(--accent-magenta)]" :
                    c.accent === "cyan"    ? "text-[color:var(--accent-cyan)]" :
                                             "text-[color:var(--accent-yellow)]";
                  const starter = CLASS_STARTERS[c.name];
                  const isSelected = c.name === value;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      data-testid={`class-option-${c.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      onClick={() => { onChange(c.name); setOpen(false); }}
                      className={`text-left border-2 ${accentBorder} p-4 hover:bg-[color:var(--bg-panel-2)] transition-colors ${isSelected ? "ring-2 ring-offset-2 ring-offset-[color:var(--bg-void)] ring-[color:var(--accent-yellow)]" : ""}`}
                    >
                      <div className={`font-display font-black uppercase tracking-tight text-lg leading-tight ${accentText}`}>
                        {c.name}
                      </div>
                      <div className="font-mono text-xs text-[color:var(--text-secondary)] mt-1 leading-snug">
                        {c.desc}
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-1 text-[10px] font-mono uppercase tracking-widest">
                        <div>
                          <span className="text-[color:var(--text-muted)]">STATS </span>
                          <span className="text-[color:var(--text-primary)]">{statLine(c) || "3d6"}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--text-muted)]">CREDS </span>
                          <span className="text-[color:var(--accent-yellow)]">{formulaLabel(credsFormulaFor(c.name))}</span>
                          <span className="text-[color:var(--text-muted)]"> · DEBT </span>
                          <span className="text-[color:var(--accent-magenta)]">{formulaLabel(debtFormulaFor(c.name))}</span>
                        </div>
                        {starter && (
                          <div>
                            <span className="text-[color:var(--text-muted)]">LOADOUT </span>
                            <span className="text-[color:var(--text-primary)]">{starter.weapons.join(" / ")} · {starter.armor.join(" / ")}</span>
                          </div>
                        )}
                        {c.name === "The Burned Hacker" && (
                          <div className="text-[color:var(--accent-cyan)]">+ CYBERDECK · d6 STARTER APP</div>
                        )}
                        {c.name === "The Shunned Nanomancer" && (
                          <div className="text-[color:var(--accent-cyan)]">+ 2 RANDOM NANO POWERS</div>
                        )}
                        <div className="text-[color:var(--text-muted)]">+ 2 RANDOM GEAR PICKS · GLITCHES d{c.glitches?.sides || 2}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 sm:px-6 py-2 border-t-2 border-[color:var(--border-hard)] text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                Picking a class resets the sheet (name kept) and rolls a fresh loadout.
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
